// src/app/rail-state.ts
// The rail's two states: folded (56px, icons and instant tooltips) or with labels (232px).
// Design log 12 Oct, Rule "Rail expand / collapse" (prototype src/app/shell/railState.ts): labels by
// default on windows 2200px and wider; the toggle on the logo, or the `[` key, overrides that and is
// remembered in localStorage (wrapped: a private window or blocked storage throws, and the rail must
// still work without it). One shared store, so every control that folds the rail moves the same rail;
// the `[` key and the resize listener attach once, with the first subscriber.
import { useState, useSyncExternalStore } from 'react';

export const RAIL_STORAGE_KEY = 'scrips.navigationRail.expanded';

/** The rail's default when nobody has chosen: labels from this viewport width (px). */
export const RAIL_EXPANDED_FROM_VIEWPORT = 2200;

export function readRailPreference(): boolean | null {
  try {
    const v = window.localStorage.getItem(RAIL_STORAGE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeRailPreference(expanded: boolean): void {
  try {
    window.localStorage.setItem(RAIL_STORAGE_KEY, expanded ? '1' : '0');
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

export function railDefaultFor(viewport: number): boolean {
  return viewport >= RAIL_EXPANDED_FROM_VIEWPORT;
}

/** True while focus is in a place that takes typed text, so `[` there types a bracket. */
function typing(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}

let chosen: boolean | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

function onKey(e: KeyboardEvent) {
  if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
  e.preventDefault();
  toggleRail();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', emit);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', emit);
    }
  };
}

/** Subscribe to the rail's state (its choice, the width default, a fit fold). */
export const subscribeRailState = (listener: () => void): (() => void) => subscribe(listener);

/** The clinician's own state for the rail (their choice, else the width default), ignoring a fit fold. */
export function railPreferredExpanded(): boolean {
  return chosen ?? railDefaultFor(typeof window === 'undefined' ? 0 : window.innerWidth);
}

/** The encounter's zoning folds the rail while its centre would drop under the 480 floor (`?x=intel-always-open`,
 *  src/encounter/encounter-zoning.ts; Antonin's R-LAY-04: the nav rail folds first). Not stored: the clinician's own choice
 *  is kept and comes back once the fold is lifted. */
let fitFolded = false;
export function setRailFitFolded(next: boolean): void {
  if (fitFolded === next) return;
  fitFolded = next;
  emit();
}

/** Folded or with labels, as the rail shows it now. */
export function railExpanded(): boolean {
  return fitFolded ? false : railPreferredExpanded();
}

/** Choose a state (remembered when storage allows). */
export function setRailExpanded(expanded: boolean): void {
  chosen = expanded;
  writeRailPreference(expanded);
  emit();
}

export function toggleRail(): void {
  setRailExpanded(!railExpanded());
}

/** Test seam: forget the choice made on this page. */
export function resetRailChoice(): void {
  chosen = null;
  fitFolded = false;
  emit();
}

export function useRailExpanded(): [boolean, () => void] {
  // A fresh page (no subscriber yet) reads the remembered choice; later consumers share the live one,
  // so a storage that throws never resets a choice made on this page.
  useState(() => {
    if (listeners.size === 0 && typeof window !== 'undefined') chosen = readRailPreference();
    return 0;
  });
  const expanded = useSyncExternalStore(subscribe, railExpanded, () => false);
  return [expanded, toggleRail];
}
